import { useRef, useState, type ReactNode } from 'react';
import { Camera, Plus, Trash2 } from '../../icons';
import { avatarImageUrl } from '../../lib/avatar-src';
import { putPersonAvatar, deletePersonAvatar } from '../../api/hr-people';
import './PersonHead.css';

/**
 * 사람 상세의 **머리** — 사진과 신원이 늘 서 있는 자리 (2026-09-02 · 시안 승인).
 *
 * ── 왜 생겼나 ────────────────────────────────────────────────────────────
 * ★★ 두 화면(직원 인사 카드 · 직원 관리 상세) 다 **맨 위가 이름 한 줄**이었고, 사람의 기본
 *    정보는 「신상」 탭을 눌러야 나왔다. 담당자가 발령·경력·포상을 훑는 동안 **사번도 소속도
 *    화면에 없어서**, 옆 사람의 기록과 헷갈리지 않으려면 탭을 왕복해야 했다.
 * ★★ 그리고 **사진을 아예 안 그렸다** — 서버는 `avatarUrl` 을 주고 남의 사진을 볼 길
 *    (`GET /users/:id/avatar`)까지 있었는데도.
 *
 * ── 규율 ────────────────────────────────────────────────────────────────
 * ★★★ **사진을 바꾸는 단추는 늘 보인다.** 정본 P5 가 hover 로만 나타나는 조작을 금지한다 —
 *    사진 위에 덮개를 씌웠다가 마우스를 올려야 뜨는 방식은 **터치 기기에서 아예 못 찾는다.**
 * ★★ **한 벌만 둔다.** 두 화면이 이 컴포넌트를 나눠 쓴다. 복사해 두면 한쪽만 고쳐지는 날이
 *    온다 — 인사기록 표가 실제로 그렇게 갈라져 있었다(#938).
 * ★ **없는 칸은 그리지 않는다.** 범위 밖이면 서버가 키를 안 준다. 「—」로 그리면
 *   「비어 있다」로 읽혀 담당자가 채우러 간다.
 */

export interface PersonFact {
  label: string;
  value: ReactNode;
  /** 자리가 흔들리면 안 되는 칸(날짜·사번·번호). */
  numeric?: boolean;
  /** 한 줄을 통째로 쓴다 — 근무제처럼 값이 여럿 붙는 칸. */
  wide?: boolean;
}

export interface PersonHeadProps {
  userId: string;
  name: string;
  avatarUrl?: string | null;
  /** 이름 아래 한 줄 — 소속 · 직위 · 사번 · 근속. */
  subtitle?: ReactNode;
  /** 이름 옆 딱지 — 재직 · 팀장 · 고용형태 · 권한. 셋을 넘기지 않는다. */
  badges?: ReactNode;
  /** 머리에 늘 두는 몇 칸(연락과 신원). 나머지는 「인사 정보」 탭이 맡는다. */
  facts?: ReadonlyArray<PersonFact>;
  /** 오른쪽 — 이 화면에서 하는 일이 아니라 **가는 길**(발령 등록 · 증명서). */
  actions?: ReactNode;
  /** 왼쪽 위 — 「← 목록」처럼 돌아가는 길. */
  back?: ReactNode;
  /** 머리 아래 한 줄 — 「열면 기록이 남습니다」 같은 사실. */
  note?: ReactNode;
  /**
   * 사진을 바꿀 수 있나. **인사 담당(전사를 보는 사람)만** 참이다 —
   * 서버도 같은 판정을 하므로(403) 못 바꾸는 사람에게는 단추를 안 그린다.
   */
  canEditAvatar?: boolean;
  /** 사진이 바뀌면 부모가 카드를 다시 읽는다. */
  onAvatarChange?: (avatarUrl: string | null) => void;
  className?: string;
}

/** 서버가 지키는 규격 그대로 — 두 길이 다른 규격이면 안 된다. */
const MIME = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;

const ERRORS: Record<number, string> = {
  403: '사진을 바꿀 권한이 없습니다.',
  413: '이미지는 2MB 이하만 올릴 수 있습니다.',
  415: 'PNG · JPEG · WEBP 만 올릴 수 있습니다.',
  400: '이미지 형식이 올바르지 않습니다.',
  502: '이미지 저장에 실패했습니다 — 잠시 뒤 다시 시도해 주세요.',
};

export function PersonHead({
  userId, name, avatarUrl, subtitle, badges, facts, actions, back, note,
  canEditAvatar = false, onAvatarChange, className,
}: PersonHeadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const src = avatarImageUrl(userId, avatarUrl);
  const initial = name.trim().slice(0, 1) || '?';

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true); setError(null);
    try {
      onAvatarChange?.(await fn());
      setMenu(false);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status ?? 0;
      setError(ERRORS[status] ?? '사진을 바꾸지 못했습니다.');
    } finally { setBusy(false); }
  };

  const pick = (file: File | undefined) => {
    if (!file) return;
    // ★ 서버가 막기 전에 화면이 먼저 말한다 — 2MB 를 올려 보내고 413 을 받는 것보다 낫다.
    if (!MIME.includes(file.type)) { setError('PNG · JPEG · WEBP 만 올릴 수 있습니다.'); return; }
    if (file.size > MAX_BYTES) { setError('이미지는 2MB 이하만 올릴 수 있습니다.'); return; }
    void run(async () => (await putPersonAvatar(userId, file)).avatarUrl);
  };

  return (
    <header className={`phead${className ? ` ${className}` : ''}`}>
      {back && <div className="phead__back">{back}</div>}

      <div className="phead__body">
        <div className="phead__avcol">
          <div className="phead__avwrap">
            <div className="phead__av">
              {src
                ? <img src={src} alt={`${name} 사진`} />
                : <span aria-hidden>{initial}</span>}
            </div>
            {canEditAvatar && (
              /* ★ 늘 보인다 — hover 로만 나타나는 조작은 정본 P5 위반이고 터치에서 못 찾는다. */
              <button
                type="button"
                className="phead__avbtn"
                aria-label={src ? `${name} 사진 바꾸기` : `${name} 사진 등록`}
                aria-expanded={menu}
                disabled={busy}
                onClick={() => { setError(null); setMenu((v) => !v); }}
              >
                {src ? <Camera size={15} aria-hidden /> : <Plus size={15} aria-hidden />}
              </button>
            )}
          </div>

          {canEditAvatar && menu && (
            <div className="phead__menu" role="group" aria-label="사진">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}>
                사진 고르기…
              </button>
              {src && (
                <button
                  type="button" className="phead__menu-danger" disabled={busy}
                  onClick={() => { void run(async () => (await deletePersonAvatar(userId)).avatarUrl); }}
                >
                  <Trash2 size={13} aria-hidden />사진 지우기
                </button>
              )}
              <p className="phead__menu-note">PNG · JPEG · WEBP · 2MB 이하<br />정사각으로 잘립니다</p>
            </div>
          )}

          <input
            ref={fileRef} type="file" accept={MIME.join(',')} className="phead__file"
            aria-hidden tabIndex={-1}
            onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }}
          />
        </div>

        <div className="phead__main">
          <div className="phead__name">
            <b>{name}</b>
            {badges}
          </div>
          {subtitle != null && <p className="phead__sub">{subtitle}</p>}

          {facts && facts.length > 0 && (
            <dl className="phead__facts">
              {facts.map((f) => (
                <div
                  key={f.label}
                  className={`phead__fact${f.wide ? ' phead__fact--wide' : ''}`}
                >
                  <dt>{f.label}</dt>
                  <dd className={f.numeric ? 'phead__num' : undefined}>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        {actions && <div className="phead__act">{actions}</div>}
      </div>

      {error && <p className="phead__err" role="alert">{error}</p>}
      {note != null && <p className="phead__note">{note}</p>}
    </header>
  );
}
